import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TradeExecutionService from '../../services/TradeExecutionService';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 'medium',
  padding: '8px 12px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function TradeExecutionWidget() {
  const navigate = useNavigate();
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchExecutions = async () => {
      try {
        setLoading(true);
        const data = await TradeExecutionService.getLiveExecutions();
        setExecutions(data.executions.slice(0, 5)); // Show only the most recent 5 executions
        setError(null);
      } catch (err) {
        console.error('Error fetching executions for dashboard:', err);
        setError('Failed to load recent executions');
      } finally {
        setLoading(false);
      }
    };

    fetchExecutions();
  }, []);

  // Format timestamp to readable time
  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp).toLocaleTimeString();
  };

  // Get status chip with appropriate color
  const renderStatusChip = (status) => {
    let color;
    
    switch (status.toLowerCase()) {
      case 'completed':
        color = 'success';
        break;
      case 'pending':
        color = 'warning';
        break;
      case 'failed':
        color = 'error';
        break;
      case 'cancelled':
        color = 'default';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        size="small" 
        color={color} 
        label={status.charAt(0).toUpperCase() + status.slice(1)} 
      />
    );
  };

  if (loading) {
    return (
      <Paper sx={{ p: 2, height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={30} sx={{ mr: 2 }} />
        <Typography variant="body1">Loading executions...</Typography>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="h6" gutterBottom>Recent Executions</Typography>
        <Typography variant="body1" color="error">{error}</Typography>
        <Button 
          variant="outlined" 
          size="small" 
          startIcon={<RefreshIcon />} 
          onClick={() => window.location.reload()}
          sx={{ mt: 2 }}
        >
          Refresh
        </Button>
      </Paper>
    );
  }

  if (executions.length === 0) {
    return (
      <Paper sx={{ p: 2, height: '100%' }}>
        <Typography variant="h6" gutterBottom>Recent Executions</Typography>
        <Typography variant="body1">No recent executions found.</Typography>
        <Button 
          variant="contained" 
          onClick={() => navigate('/user/execution')}
          startIcon={<PlayArrowIcon />}
          sx={{ mt: 2 }}
        >
          Go to Trade Execution
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Recent Executions</Typography>
        <Button 
          variant="text" 
          size="small" 
          endIcon={<SwapVertIcon />}
          onClick={() => navigate('/user/execution')}
        >
          View All
        </Button>
      </Box>
      
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>Time</StyledTableCell>
              <StyledTableCell>Strategy</StyledTableCell>
              <StyledTableCell>Symbol</StyledTableCell>
              <StyledTableCell>Action</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {executions.map((execution) => (
              <StyledTableRow key={execution.id} hover>
                <TableCell>{formatTime(execution.timestamp)}</TableCell>
                <TableCell>
                  <Chip 
                    size="small" 
                    label={execution.strategy_name} 
                    variant="outlined"
                    color={
                      execution.market === 'Crypto' ? 'secondary' :
                      execution.market === 'Stocks' ? 'primary' :
                      'default'
                    }
                  />
                </TableCell>
                <TableCell>{execution.symbol}</TableCell>
                <TableCell>
                  <Chip 
                    size="small" 
                    label={execution.action.toUpperCase()}
                    color={execution.action === 'buy' ? 'success' : 'error'}
                  />
                </TableCell>
                <TableCell>{renderStatusChip(execution.status)}</TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default TradeExecutionWidget;
